/* eslint-disable no-nested-ternary */
/* eslint-disable no-underscore-dangle */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import DataTable from "components/DataTable";
import { useDispatch } from "react-redux";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MenuItem from "@mui/material/MenuItem";
import fluxQuerys from "../analyticsCustomSearch/AnalyticsFluxQuery";
import helpers from "../AnalyticsHelpers";
import Loading from "../Loading";
import CardMetric from "./CardMetric";

const ALL_APARTMENTS_VALUE = "ALL_APARTMENS";

const selectSx = {
  "& .MuiInputBase-root": {
    height: 44,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    padding: "10.5px 14px",
  },
};

function LastHourlyStateProject({ project, projectId, deviceIds, apartments, darkMode, t }) {
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [warn, setWarn] = useState(null);
  const [hasQueried, setHasQueried] = useState(false);
  const [apartmentSel, setApartmentSel] = useState(ALL_APARTMENTS_VALUE);
  const [appliedApartmentSel, setAppliedApartmentSel] = useState(ALL_APARTMENTS_VALUE);

  const enabledMetrics = useMemo(
    () => helpers.getEnabledMetrics(project?.metricsConfig || {}),
    [project?.metricsConfig]
  );

  const enabledMetricKeys = useMemo(
    () => enabledMetrics.map((metric) => metric.key),
    [enabledMetrics]
  );

  const aptList = useMemo(() => helpers.getApartList(apartments), [apartments]);

  const apartmentsById = useMemo(
    () => new Map(aptList.map((apartment) => [apartment.apartmentId, apartment])),
    [aptList]
  );

  const apartmentOptions = useMemo(() => {
    const allApartOption = {
      label: t("allApartments"),
      value: ALL_APARTMENTS_VALUE,
      apartmentId: ALL_APARTMENTS_VALUE,
    };

    return [allApartOption, ...aptList];
  }, [aptList, t]);

  useEffect(() => {
    const validValues = new Set(apartmentOptions.map((option) => option.value));

    if (!validValues.has(apartmentSel)) {
      setApartmentSel(ALL_APARTMENTS_VALUE);
    }

    if (!validValues.has(appliedApartmentSel)) {
      setAppliedApartmentSel(ALL_APARTMENTS_VALUE);
      setRows([]);
    }
  }, [apartmentOptions, apartmentSel, appliedApartmentSel]);

  const columns = useMemo(
    () => [
      { Header: t("apartment"), accessor: "apartment", align: "center" },
      ...enabledMetrics.map((col) => {
        const headerName = helpers.getMetricTitleLabel({
          metricKey: col.key,
          metricsConfig: project?.metricsConfig || {},
          t,
        });
        return {
          Header: headerName,
          accessor: col.key,
          align: "center",
          Cell: ({ value }) =>
            col?.formatedValueToString ? col?.formatedValueToString(value) : value ?? "—",
        };
      }),
    ],
    [enabledMetrics, project?.metricsConfig, t]
  );

  const fetchData = useCallback(
    (targetApartmentSelection) => {
      const isAllApartmentsMode = targetApartmentSelection === ALL_APARTMENTS_VALUE;
      const selectedApartmentId = isAllApartmentsMode ? null : targetApartmentSelection;

      setLoading(true);
      setWarn(null);
      setRows([]);
      setHasQueried(true);
      setAppliedApartmentSel(targetApartmentSelection);

      if (enabledMetricKeys.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      const selectedApartment = isAllApartmentsMode
        ? null
        : apartmentsById.get(selectedApartmentId);

      const deviceList = isAllApartmentsMode
        ? deviceIds?.length
          ? deviceIds
          : aptList.map((apt) => apt.deviceId).filter(Boolean)
        : selectedApartment?.deviceId
        ? [selectedApartment.deviceId]
        : [];

      if (!projectId || !deviceList.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      const queryApartmentId = isAllApartmentsMode ? null : selectedApartment?.normalizeApartmentId;

      const lastState1HourCloudQueryFlux = fluxQuerys.proyectLastState1HourFlux(
        enabledMetricKeys,
        queryApartmentId
      );

      dispatch({
        type: "MAKE_QUERY",
        payload: {
          source: "cloud",
          projectId,
          deviceIds: deviceList,
          flux: lastState1HourCloudQueryFlux,
          cb: (res) => {
            try {
              const responseRows = helpers.extractRows(res);
              const valuesByApartment = helpers.buildValuesByApartment(responseRows);

              if (isAllApartmentsMode) {
                const allApartmentsRows = aptList.map((apt) => {
                  const baseRow = { apartmentId: apt?.apartmentId, apartment: apt?.label };
                  const apartmentValues = valuesByApartment.get(apt?.normalizeApartmentId);
                  const preferredApartmentValues = helpers.getPreferredOnnaValues(
                    apartmentValues,
                    apt?.onnaId
                  );

                  const row = helpers.assignMetricValues(
                    baseRow,
                    enabledMetrics,
                    preferredApartmentValues?.meansurementsValuesToShow || {}
                  );

                  return row;
                });

                setRows(allApartmentsRows);
              } else {
                const selectedApartId = selectedApartment?.normalizeApartmentId;
                const selectedApartmentLabel = selectedApartment?.label || selectedApartmentId;
                const selectedApartmentValuesBySource = valuesByApartment.get(selectedApartId);

                if (!selectedApartmentValuesBySource) {
                  setRows([]);
                  return;
                }

                const preferredApartmentValues = helpers.getPreferredOnnaValues(
                  selectedApartmentValuesBySource,
                  selectedApartment?.onnaId
                );

                if (!preferredApartmentValues) {
                  setRows([]);
                  return;
                }

                const selectedApartmentValues =
                  preferredApartmentValues.meansurementsValuesToShow || {};
                const selectedApartmentUpdatedAt = preferredApartmentValues.updatedAt || null;

                const baseRow = {
                  apartmentId: selectedApartmentId,
                  apartment: selectedApartmentLabel,
                  updatedAt: selectedApartmentUpdatedAt,
                };

                const row = helpers.assignMetricValues(
                  baseRow,
                  enabledMetrics,
                  selectedApartmentValues
                );

                const hasAny = helpers.hasAnyMetricValue(row, enabledMetrics);

                setRows(hasAny ? [row] : []);
              }
            } catch (e) {
              setWarn(t("influxErrorRequest"));
            } finally {
              setLoading(false);
            }
          },
        },
      });
    },
    [apartmentsById, aptList, deviceIds, dispatch, enabledMetricKeys, enabledMetrics, projectId, t]
  );

  const isAllApartments = appliedApartmentSel === ALL_APARTMENTS_VALUE;
  const dataToShow = rows[0];

  if (enabledMetricKeys.length === 0) {
    return (
      <MDBox
        style={{
          border: darkMode ? "1px solid #26304b" : "1px solid #e5e7eb",
          background: darkMode ? "#26304b" : "#ffffff",
          borderRadius: 14,
          padding: 16,
        }}
      >
        {t("noMetricsConfiguredToShow")}
      </MDBox>
    );
  }

  const renderResult = () => {
    if (!hasQueried) return null;

    if (loading) {
      return <Loading darkMode={darkMode} t={t} />;
    }

    if (!dataToShow) {
      return (
        <MDBox
          style={{
            border: darkMode ? "1px solid #26304b" : "1px solid #e5e7eb",
            background: darkMode ? "#26304b" : "#ffffff",
            borderRadius: 14,
            padding: 16,
          }}
        >
          {t("noPossibleShowData")}
        </MDBox>
      );
    }

    if (isAllApartments) {
      return (
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={
              rows.length > 10 ? { defaultValue: 10, entries: [5, 10, 15, 20, 25] } : false
            }
            showTotalEntries={rows.length > 10}
            canSearch={rows.length > 10}
            canSelect={false}
            noEndBorder
            loading={loading}
          />
        </MDBox>
      );
    }

    const selectedApartmentRow = rows[0];

    const selectedApartmentDate = helpers.formatAnalyticsDate({
      ts: selectedApartmentRow?.updatedAt,
      queryPeriod: "hourly",
      timeZone: project?.timeZone || "UTC",
    });

    return (
      <MDBox
        style={{
          border: darkMode ? "1px solid #26304b" : "1px solid #e5e7eb",
          background: darkMode ? "#26304b" : "#ffffff",
          borderRadius: 16,
          padding: 18,
          boxShadow: darkMode ? "none" : "0 6px 18px rgba(12,46,84,.06)",
          width: "100%",
        }}
      >
        <MDBox
          style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
        >
          <MDBox style={{ fontWeight: 800, fontSize: 18, color: darkMode ? "#f8fafc" : "#0f172a" }}>
            {selectedApartmentRow?.apartment}
          </MDBox>

          {selectedApartmentRow?.updatedAt && (
            <MDTypography style={{ fontSize: 12, color: darkMode ? "#f8fafc" : "#0f172a" }}>
              {`${t("lastData")}: ${selectedApartmentDate}`}
            </MDTypography>
          )}
        </MDBox>

        <MDBox
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 12,
            marginTop: 14,
          }}
        >
          {enabledMetrics.map((measurement) => {
            const measurementKey = measurement?.key;
            const formatedValue = measurement?.formatedValueToString(
              selectedApartmentRow[measurementKey]
            );

            if (!formatedValue) return null;

            const effectiveSuffix = helpers.getMetricSuffix(measurementKey, project?.metricsConfig);
            const metricLabel = helpers.getMetricDisplayLabel({
              metricKey: measurementKey,
              metricsConfig: project?.metricsConfig || {},
              t,
            });

            return (
              <CardMetric
                key={measurementKey}
                measurementKey={measurementKey}
                metricLabel={metricLabel}
                effectiveSuffix={effectiveSuffix}
                formatedValue={formatedValue}
                darkMode={darkMode}
              />
            );
          })}
        </MDBox>
      </MDBox>
    );
  };

  return (
    <MDBox>
      <MDBox
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          mt: 2,
          mb: 2,
          alignItems: "end",
        }}
      >
        <MDBox display="flex" flexDirection="column" gap={1}>
          <MDTypography variant="medium" fontWeight="regular" color="text">
            {t("apartment")}
          </MDTypography>

          <MDInput
            select
            fullWidth
            variant="outlined"
            value={apartmentSel}
            onChange={(event) => setApartmentSel(event.target.value)}
            SelectProps={{ MenuProps: { disableScrollLock: true } }}
            sx={selectSx}
          >
            {apartmentOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </MDInput>
        </MDBox>

        <MDBox display="flex" alignItems="flex-end" justifyContent="flex-end">
          <MDButton
            color="info"
            variant="gradient"
            size="medium"
            disabled={loading}
            onClick={() => fetchData(apartmentSel)}
          >
            {t("makeQuery")}
          </MDButton>
        </MDBox>
      </MDBox>

      {warn ? (
        <MDBox
          style={{
            background: darkMode ? "#1a2035" : "#ffffff",
            color: darkMode ? "#ffffff" : "#1a2035",
            padding: "8px 12px",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          {warn}
        </MDBox>
      ) : null}

      {renderResult()}
    </MDBox>
  );
}

LastHourlyStateProject.defaultProps = {
  projectId: "",
  deviceIds: [],
  apartments: {},
  project: {},
  darkMode: false,
  t: () => {},
};

LastHourlyStateProject.propTypes = {
  projectId: PropTypes.string,
  deviceIds: PropTypes.arrayOf(PropTypes.string),
  apartments: PropTypes.object,
  project: PropTypes.object,
  darkMode: PropTypes.bool,
  t: PropTypes.func,
};

export default LastHourlyStateProject;
